import React from 'react';
import { FileText, Shield, AlertCircle, CheckCircle, Clock, Ban } from 'lucide-react';

interface TermsProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsOfService: React.FC<TermsProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <FileText size={28} style={{ color: '#2575fc' }} />
          <h2 style={styles.title}>Пользовательское соглашение</h2>
          <button style={styles.closeButton} onClick={onClose}>✕</button>
        </div>

        <div style={styles.content}>
          <div style={styles.lastUpdated}>Последнее обновление: 27 сентября 2026 г.</div>

          {/* Раздел 1 */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><FileText size={20} /></div>
            <h3 style={styles.sectionTitle}>1. Общие положения</h3>
          </div>
          <p style={styles.text}>
            1.1. Настоящее Пользовательское соглашение (далее — Соглашение) регулирует отношения между 
            <strong> Индивидуальным предпринимателем Наймушиной Евгенией Вадимовной</strong> 
            (ИНН 773379346085, ОГРНИП 323784700299018), сокращенно — <strong>ИП Наймушина Е.В.</strong> 
            (далее — Администрация), и физическим или юридическим лицом (далее — Пользователь), 
            возникающие при использовании сервиса <strong>HR Лама</strong> (далее — Сервис), 
            доступного по адресу: <a href="https://hrlama.ru" style={styles.link}>hrlama.ru</a>.
          </p>
          <p style={styles.text}>
            1.2. Используя Сервис, Пользователь полностью и безоговорочно принимает условия настоящего Соглашения.
          </p>
          <p style={styles.text}>
            1.3. Администрация оставляет за собой право вносить изменения в Соглашение без предварительного уведомления. 
            Новая редакция вступает в силу с момента размещения на Сайте.
          </p>

          {/* Раздел 2 */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Shield size={20} /></div>
            <h3 style={styles.sectionTitle}>2. Предмет соглашения</h3>
          </div>
          <p style={styles.text}>
            2.1. Администрация предоставляет Пользователю доступ к HR-платформе «Лама HR», 
            включающей следующие модули:
          </p>
          <ul style={styles.list}>
            <li><strong>Рекрутинг и найм:</strong> CRM для рекрутинга (кандидаты, вакансии, заявки), поиск резюме с hh.ru, Data-Driven Recruitment, проверка соответствия, помощник проведения интервью, расширение Chrome для hh.ru, superjob.ru и avito.ru;</li>
            <li><strong>Оценка и развитие персонала:</strong> психодиагностика, психологические тесты, DISC (поведенческий профиль), KPI-конструктор, E-Learning платформа (курсы, тесты, обучающие программы);</li>
            <li><strong>Аналитика и отчётность:</strong> BI-дашборды, конструктор отчётов и диаграмм, HR-метрики, аналитика зарплат;</li>
            <li><strong>КДП — кадровое делопроизводство:</strong> управление документами, сотрудниками, отпусками, ознакомлениями;</li>
            <li><strong>Sales CRM:</strong> управление сделками, задачами, согласованиями, клиентской базой;</li>
            <li><strong>Бухгалтерия:</strong> заявки, счета, платежи, платёжный календарь;</li>
            <li><strong>HRM:</strong> управление персоналом, оргструктура, заявки на подбор, вакансии, клиенты;</li>
            <li><strong>Вспомогательные инструменты:</strong> шаблоны документов, конвертер PDF в Word.</li>
          </ul>

          {/* Раздел 3 */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><CheckCircle size={20} /></div>
            <h3 style={styles.sectionTitle}>3. Регистрация и аккаунт</h3>
          </div>
          <p style={styles.text}>
            3.1. Для доступа к Сервису Пользователь обязан пройти процедуру регистрации, 
            предоставив достоверные данные.
          </p>
          <p style={styles.text}>
            3.2. Пользователь несет ответственность за сохранность своего логина и пароля. 
            Все действия, совершенные с использованием учетной записи Пользователя, 
            считаются совершенными Пользователем лично.
          </p>
          <p style={styles.text}>
            3.3. Запрещается передача учетной записи третьим лицам. 
            Один аккаунт — один Пользователь.
          </p>

          <p style={styles.text}>
            3.4. Если Пользователь не использует Сервис (не совершает вход в учетную запись
            и не совершает действий в модулях Сервиса) в течение <strong>1 (одного) календарного месяца</strong>
            с момента регистрации или последней активности, Администрация вправе инициировать
            удаление учетной записи и связанных данных.
          </p>
          <p style={styles.text}>
            3.4.1. Удаление производится <strong>вручную</strong> уполномоченным сотрудником
            Администрации (супер-администратором). Автоматическое удаление по истечении
            срока неактивности не применяется.
          </p>
          <p style={styles.text}>
            3.4.2. Перед удалением Администрация направляет Пользователю <strong>уведомление</strong>
            на email, указанный при регистрации, с предупреждением об удалении данных.
            Пользователю предоставляется <strong>3 (три) календарных дня</strong> для возобновления
            активности, выгрузки данных или ответа на уведомление.
          </p>
          <p style={styles.text}>
            3.5. Под удалением понимается безвозвратное удаление учетной записи,
            всех загруженных файлов (резюме, документы, шаблоны), внесенных записей
            (кандидаты, сделки, счета, сотрудники, результаты тестов) и настроек.
          </p>
          <p style={styles.text}>
            3.5.1. Если в течение <strong>3 (трех) календарных дней</strong> с момента отправки
            уведомления Пользователь не возобновил активность, не выгрузил данные и не направил
            Администрации мотивированный ответ, данные удаляются вручную.
            Восстановление удаленных данных не производится.
          </p>
          <p style={styles.text}>
            3.5.2. Рекомендуем самостоятельно выгружать необходимые данные при получении
            уведомления о предстоящем удалении.
          </p>

          {/* Раздел 4 */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Ban size={20} /></div>
            <h3 style={styles.sectionTitle}>4. Права и обязанности Пользователя</h3>
          </div>
          <p style={styles.text}><strong>Пользователь обязуется:</strong></p>
          <ul style={styles.list}>
            <li>Предоставлять достоверную информацию при регистрации и использовании Сервиса;</li>
            <li>Не использовать Сервис для распространения спама, вредоносного ПО и запрещенного контента;</li>
            <li>Не нарушать права интеллектуальной собственности Администрации и третьих лиц;</li>
            <li>Не предпринимать попыток взлома, обхода систем защиты или нарушения работы Сервиса;</li>
            <li>Соблюдать законодательство РФ при использовании Сервиса.</li>
          </ul>
          <p style={styles.text}><strong>Пользователь имеет право:</strong></p>
          <ul style={styles.list}>
            <li>Использовать функционал Сервиса в соответствии с выбранным тарифом;</li>
            <li>Обращаться в службу поддержки по вопросам работы Сервиса;</li>
            <li>В любое время прекратить использование Сервиса и удалить свою учетную запись;</li>
            <li>Получать информацию о своей учетной записи и персональных данных.</li>
          </ul>

          {/* Раздел 5 */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><AlertCircle size={20} /></div>
            <h3 style={styles.sectionTitle}>5. Права и обязанности Администрации</h3>
          </div>
          <p style={styles.text}><strong>Администрация вправе:</strong></p>
          <ul style={styles.list}>
            <li>Изменять функционал Сервиса, тарифы и условия обслуживания;</li>
            <li>Блокировать учетные записи Пользователей, нарушающих Соглашение;</li>
            <li>Удалять контент, противоречащий законодательству или Соглашению;</li>
            <li>Проводить технические работы с временным ограничением доступа к Сервису;</li>
            <li>Отказать в обслуживании без объяснения причин;</li>
                 <li>Удалять учетные записи и связанные с ними данные при неактивности Пользователя 
                     более 1 (одного) календарного месяца. Удаление производится вручную уполномоченным 
                     сотрудником Администрации после направления Пользователю предварительного уведомления 
                     и истечения 3 (трех) календарных дней с момента уведомления (п. 3.4–3.5).</li>
          </ul>
          <p style={styles.text}><strong>Администрация обязуется:</strong></p>
          <ul style={styles.list}>
            <li>Обеспечивать работоспособность Сервиса в соответствии с заявленными характеристиками;</li>
            <li>Обеспечивать конфиденциальность и безопасность персональных данных Пользователей;</li>
            <li>Предоставлять техническую поддержку по запросам Пользователей;</li>
            <li>Уведомлять Пользователей о существенных изменениях в работе Сервиса.</li>
          </ul>

          {/* Раздел 6 */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Clock size={20} /></div>
            <h3 style={styles.sectionTitle}>6. Финансовые условия</h3>
          </div>
          <p style={styles.text}>
            6.1. Сервис предоставляется на платной основе. Актуальные тарифы размещены на Сайте.
          </p>
          <p style={styles.text}>
            6.2. Пользователь вправе воспользоваться бесплатным демо-доступом сроком на 5 (пять) календарных дней.
          </p>
          <p style={styles.text}>
            6.3. Оплата производится в порядке предоплаты за выбранный период (месяц, квартал, год).
          </p>
          <p style={styles.text}>
            6.4. Возврат денежных средств производится в соответствии с законодательством РФ.
          </p>
          <p style={styles.text}>
            6.5. Цена указана за одного пользователя. Количество пользователей может быть увеличено 
            по согласованию с Администрацией.
          </p>

          {/* Раздел 7 */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><Shield size={20} /></div>
            <h3 style={styles.sectionTitle}>7. Интеллектуальная собственность</h3>
          </div>
          <p style={styles.text}>
            7.1. Все права на Сервис, его исходный код, дизайн, логотипы, тексты, методики тестирования, 
            базы данных принадлежат Администрации.
          </p>
          <p style={styles.text}>
            7.2. Пользователь не вправе копировать, распространять, модифицировать или использовать 
            элементы Сервиса без письменного разрешения Администрации.
          </p>
          <p style={styles.text}>
            7.3. Данные, загруженные Пользователем в Сервис (резюме, вакансии, результаты тестов), 
            принадлежат Пользователю. Администрация использует их исключительно для обеспечения 
            работы Сервиса.
          </p>

          {/* Раздел 8 */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><AlertCircle size={20} /></div>
            <h3 style={styles.sectionTitle}>8. Ограничение ответственности</h3>
          </div>
          <p style={styles.text}>
            8.1. Сервис предоставляется по принципу «как есть» (as is). Администрация не гарантирует 
            отсутствие ошибок и перебоев в работе.
          </p>
          <p style={styles.text}>
            8.2. Администрация не несет ответственности за убытки, возникшие в результате 
            использования или невозможности использования Сервиса.
          </p>
          <p style={styles.text}>
            8.3. Администрация не несет ответственности за содержание данных, загруженных Пользователем.
          </p>

          {/* Раздел 9 */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><FileText size={20} /></div>
            <h3 style={styles.sectionTitle}>9. Конфиденциальность</h3>
          </div>
          <p style={styles.text}>
            9.1. Обработка персональных данных Пользователей осуществляется в соответствии 
            с Политикой обработки персональных данных, размещенной на Сайте.
          </p>
          <p style={styles.text}>
            9.2. Принимая настоящее Соглашение, Пользователь дает согласие на обработку 
            своих персональных данных в соответствии с Политикой конфиденциальности.
          </p>

          <p style={styles.text}>
            9.3. При удалении учетной записи по причине неактивности (п. 3.4–3.5) все
            персональные данные Пользователя, хранящиеся в Сервисе, удаляются в течение
            30 (тридцати) календарных дней с момента запуска процедуры удаления,
            за исключением данных, срок хранения которых установлен законодательством РФ.
          </p>

          {/* Раздел 10 */}
          <div style={styles.section}>
            <div style={styles.sectionIcon}><FileText size={20} /></div>
            <h3 style={styles.sectionTitle}>10. Заключительные положения</h3>
          </div>
          <p style={styles.text}>
            10.1. Настоящее Соглашение регулируется законодательством Российской Федерации.
          </p>
          <p style={styles.text}>
            10.2. Все споры решаются путем переговоров. При недостижении согласия — в суде 
            по месту нахождения Администрации.
          </p>
          <p style={styles.text}>
            10.3. Соглашение действует с момента акцепта Пользователем до момента удаления учетной записи.
          </p>
          <p style={styles.text}>
            10.4. По всем вопросам обращайтесь: <a href="mailto:info@hrlama.ru" style={styles.link}>info@hrlama.ru</a>.
          </p>

          <div style={styles.footerNote}>
            <p><strong>Реквизиты Администрации:</strong></p>
            <p>
              Индивидуальный предприниматель Наймушина Евгения Вадимовна<br />
              ИНН: 773379346085<br />
              ОГРНИП: 323784700299018<br />
              Адрес: 197375, г. Санкт-Петербург, вн. тер. м.о. Коломяги, наб. Реки Каменки, д. 19, к. 1, стр. 1, кв. 341<br />
              Email: info@hrlama.ru<br />
              Сайт: hrlama.ru
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    backgroundColor: '#ffffff', borderRadius: '24px', maxWidth: '800px',
    width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 30px 60px rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(37, 117, 252, 0.2)',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: '16px',
    padding: '24px 32px', borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
    background: 'linear-gradient(135deg, rgba(37, 117, 252, 0.05), rgba(106, 17, 203, 0.05))',
    borderRadius: '24px 24px 0 0',
  },
  title: { fontSize: '22px', fontWeight: 700, color: '#1a1a2e', flex: 1, margin: 0 },
  closeButton: {
    background: 'rgba(0, 0, 0, 0.05)', border: 'none', borderRadius: '50%',
    width: '36px', height: '36px', fontSize: '18px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666',
  },
  content: { padding: '32px', overflowY: 'auto', flex: 1 },
  lastUpdated: { fontSize: '13px', color: '#999', marginBottom: '24px', fontStyle: 'italic' },
  section: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '32px', marginBottom: '16px', color: '#2575fc' },
  sectionIcon: { display: 'flex', alignItems: 'center' },
  sectionTitle: { fontSize: '18px', fontWeight: 600, color: '#1a1a2e', margin: 0 },
  text: { fontSize: '15px', lineHeight: 1.7, color: '#444', marginBottom: '16px' },
  list: { paddingLeft: '24px', margin: '0 0 16px 0', fontSize: '15px', lineHeight: 1.8, color: '#444' },
  link: { color: '#2575fc', textDecoration: 'none' },
  footerNote: {
    marginTop: '32px', padding: '20px',
    background: 'rgba(37, 117, 252, 0.05)', borderRadius: '12px',
    fontSize: '14px', lineHeight: 1.8, color: '#555',
    border: '1px solid rgba(37, 117, 252, 0.1)',
  },
};

export default TermsOfService;
